import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';

import { COMP_PLAN_EFFECTIVE_PREFIX, COMP_PLAN_NEXT_WINDOW_PREFIX } from './compPlan.constants';

const formatDate = value =>
  isNil(value) ? null : dayjs(value).format(SHORT_MONTH_DAY_YEAR_FORMAT);

// Composes the read-only comp chip summary: "<type> · <bps> bps · eff <date> ·
// next window <date>". Omits any fragment the backend left null. No projected
// $/loan anywhere — bps + windows only.
const composeSummary = ({ compensationType, basisPoints, effectiveDate, nextChangeWindow }) => {
  const effectiveLabel = formatDate(effectiveDate);
  const nextWindowLabel = formatDate(nextChangeWindow);

  return [
    compensationType,
    isNil(basisPoints) ? null : `${basisPoints} bps`,
    isNil(effectiveLabel) ? null : `${COMP_PLAN_EFFECTIVE_PREFIX} ${effectiveLabel}`,
    isNil(nextWindowLabel) ? null : `${COMP_PLAN_NEXT_WINDOW_PREFIX} ${nextWindowLabel}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Maps the comp-plan endpoint into the read-only chip display shape. Returns null
 * when there is no active plan (the endpoint 204s) so the chip is hidden instead
 * of showing fabricated terms.
 * @param {object|null} data - { compensationType, basisPoints, effectiveDate, nextChangeWindow, periodType, status }
 * @returns {object|null}
 */
export const mapCompPlan = data => {
  if (isNil(data) || isNil(data.compensationType)) {
    return null;
  }

  return {
    compensationType: data.compensationType,
    basisPoints: data.basisPoints ?? null,
    effectiveDate: formatDate(data.effectiveDate),
    nextChangeWindow: formatDate(data.nextChangeWindow),
    periodType: data.periodType ?? null,
    status: data.status ?? null,
    summary: composeSummary(data),
  };
};
